import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {LoadComponent} from '@brunata-angular/ui';
import {filterNulls, TRANSLATE} from '@brunata-angular/util';
import {CreateConsumptionAgentComponent} from '../create-consumption-agent/create-consumption-agent.component';
import {combineLatest, filter, map, Observable, shareReplay, switchMap} from 'rxjs';
import {Store} from '@ngrx/store';
import {ActivatedRoute, Router} from '@angular/router';
import {ConsumptionAgentService} from '../consumption-agent.service';
import {selectBuilding} from '../../../state/selectors';
import {
    ConsumptionAgentFormGroup,
    ConsumptionAgentRequest,
} from '../consumption-agents-overview/consumption-agent.model';
import {ToastrService} from 'ngx-toastr';
import {HasUnsavedChanges} from '@brunata-angular/ui';
import {SelectMetersForAgentService} from '../create-consumption-agent/select-meters-for-agent/select-meters-for-agent.service';

type EditConsumptionAgentView = {
    formGroupValues: ConsumptionAgentFormGroup['value'];
    buildingNo: string;
    reportId: string;
};

@Component({
    selector: 'app-edit-consumption-agent',
    standalone: true,
    template: `
        @if (view$ | async; as view) {
            <app-create-consumption-agent
                [initialFormValues]="view.formGroupValues"
                (agentRequest)="updateConsumptionAgent(view.buildingNo, view.reportId, $event)"
                (unsavedChanges)="this.hasUnsavedChangesSignal.set($event)" />
        } @else {
            <app-load />
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, LoadComponent, ReactiveFormsModule, CreateConsumptionAgentComponent],
})
export class EditConsumptionAgentComponent implements OnInit, HasUnsavedChanges {
    private readonly store = inject(Store);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly consumptionAgentService = inject(ConsumptionAgentService);
    private readonly metersForAgentService = inject(SelectMetersForAgentService);
    private readonly translate = inject(TRANSLATE);
    private readonly toastrService = inject(ToastrService);
    private readonly router = inject(Router);

    private readonly buildingNo$: Observable<string> = this.store.select(selectBuilding).pipe(
        filter(buildingState => buildingState.status === 'success'),
        map(a => a.buildingNo),
    );

    private readonly reportId$: Observable<string> = this.activatedRoute.paramMap.pipe(
        map(params => params.get('reportid')),
        filterNulls(),
        shareReplay(1),
    );

    private readonly consumptionAgent$ = combineLatest([this.buildingNo$, this.reportId$]).pipe(
        switchMap(([buildingNo, reportId]) =>
            this.consumptionAgentService
                .getConsumptionAgent(buildingNo, reportId)
                .pipe(this.consumptionAgentService.addTranslationsForAgent()),
        ),
    );

    private readonly formGroupValues$: Observable<ConsumptionAgentFormGroup['value']> =
        this.consumptionAgent$.pipe(
            map(agent => {
                const {consumptionToMonitor, meterSettings, ...rest} = agent;
                const {meterGroup, meterGroup2, usageAndUnits, ...settingsValues} = meterSettings;

                const {
                    agentName,
                    dayCount,
                    schedule,
                    sendBlank,
                    showConsumptionForMeters,
                    recipients,
                    ...investigationFormGroup
                } = rest;

                return {
                    agentName,
                    dayCount,
                    schedule,
                    sendBlank,
                    showConsumptionForMeters,
                    recipients,
                    monitoringFormGroup: {
                        consumptionToMonitor,
                        selectMetersForAgentFormGroup: {
                            meterSettingsFormGroup: {
                                ...settingsValues,
                                groupName: meterGroup.groupName,
                                groupName2: meterGroup2.groupName,
                                selectedUsageAndUnits: usageAndUnits,
                            },
                            meterIdGroupsFormGroup: {
                                idsInGroup1: meterGroup.idsInGroup,
                                idsInGroup2: meterGroup2.idsInGroup,
                            },
                        },
                    },
                    investigationFormGroup,
                };
            }),
        );

    readonly hasUnsavedChangesSignal = signal(false);

    readonly view$: Observable<EditConsumptionAgentView> = combineLatest({
        formGroupValues: this.formGroupValues$,
        buildingNo: this.buildingNo$,
        reportId: this.reportId$.pipe(map(String)),
    });

    ngOnInit(): void {
        // start with meter table visible when editing an agent
        this.metersForAgentService.meterTableVisible$.next(true);
    }

    updateConsumptionAgent(
        buildingNo: string,
        reportId: string,
        agentRequest: ConsumptionAgentRequest,
    ): void {
        this.consumptionAgentService
            .updateAgent(buildingNo, reportId, agentRequest)
            .subscribe(({agentName}) => {
                const successMessage = this.translate('monitoring_agent_updated').replace(
                    '{0}',
                    agentName,
                );

                this.toastrService.success(successMessage);
                void this.router.navigate([
                    'admin',
                    'system',
                    buildingNo,
                    'monitoring',
                    'consumption-agents',
                ]);
            });
    }

    hasUnsavedChanges(): boolean {
        return this.hasUnsavedChangesSignal();
    }
}
